import { Color } from '@ark-ui/react'
import { ColorPicker as ColorPickerPrimitive } from '@ark-ui/react/color-picker'
import { menuDataStateAnimationCls } from 'helpers'
import { useEffect, useRef, useState } from 'react'
import { cn } from 'utils/cn'

import { Switch } from '../Switch'
import { ButtonEyeDropper } from './ButtonEyeDropper'
import { ChannelInputs } from './ChannelInputs'
import { ColorPalette } from './ColorPalette'

export type TColorPickerMode = 'solid' | 'palette'

const useClampSliderPointer = () => {
	const sliderRef = useRef<HTMLDivElement>(null)
	const trackRectRef = useRef<DOMRect | null>(null)
	const activePointerIdRef = useRef<number | null>(null)

	useEffect(() => {
		const el = sliderRef.current
		if (!el) return

		const onPointerDown = (e: PointerEvent) => {
			const track = el.querySelector<HTMLElement>(
				'[data-part="channel-slider-track"]',
			)
			if (track) {
				trackRectRef.current = track.getBoundingClientRect()
				activePointerIdRef.current = e.pointerId
			}
		}

		const onPointerMove = (e: PointerEvent) => {
			if (
				activePointerIdRef.current === null ||
				e.pointerId !== activePointerIdRef.current ||
				!trackRectRef.current
			)
				return
			const { left, right } = trackRectRef.current
			const RIGHT_EDGE_BUFFER = 3
			if (e.clientX < left || e.clientX >= right - RIGHT_EDGE_BUFFER) {
				e.stopPropagation()
			}
		}

		const onPointerUp = (e: PointerEvent) => {
			if (e.pointerId === activePointerIdRef.current) {
				activePointerIdRef.current = null
				trackRectRef.current = null
			}
		}

		el.addEventListener('pointerdown', onPointerDown)
		// Capture phase so we act before Ark UI processes the event
		document.addEventListener('pointermove', onPointerMove, true)
		document.addEventListener('pointerup', onPointerUp, true)

		return () => {
			el.removeEventListener('pointerdown', onPointerDown)
			document.removeEventListener('pointermove', onPointerMove, true)
			document.removeEventListener('pointerup', onPointerUp, true)
		}
	}, [])

	return sliderRef
}

const SolidModeContent = () => {
	const sliderRef = useClampSliderPointer()

	return (
		<>
			<ColorPickerPrimitive.Area className="h-40">
				<ColorPickerPrimitive.AreaBackground className="h-full rounded-10" />
				<ColorPickerPrimitive.AreaThumb className="box-content size-4 rounded-full border-2 border-white shadow-generator outline-none outline-2" />
			</ColorPickerPrimitive.Area>
			<ColorPickerPrimitive.ChannelSlider
				ref={sliderRef}
				channel="hue"
				className="flex h-[1.375rem] items-center"
			>
				<ColorPickerPrimitive.ChannelSliderTrack className="h-3 w-full rounded-full" />
				<ColorPickerPrimitive.ChannelSliderThumb className="box-content size-[1.375rem] -translate-x-1/2 -translate-y-1/2 rounded-full border-4 border-white shadow-generator outline-none" />
			</ColorPickerPrimitive.ChannelSlider>
			<ChannelInputs />
		</>
	)
}

export const ColorPickerContent = ({
	setValue,
	containerWidth,
	initialMode,
	translations,
}: {
	setValue: (value: Color) => void
	containerWidth: number
	initialMode?: TColorPickerMode
	translations: {
		switchSolidModeLabel: string
		switchPaletteModeLabel: string
	}
}) => {
	const [mode, setMode] = useState<TColorPickerMode>(
		useRef(initialMode).current || 'solid',
	)


	const [isEyeDropperSupported, setIsEyeDropperSupported] = useState(false)

	useEffect(() => {
		if (typeof window !== 'undefined' && 'EyeDropper' in window) {
			setIsEyeDropperSupported(true)
		}
	}, [])

	return (
		<ColorPickerPrimitive.Content
			className={cn(
				menuDataStateAnimationCls,
				'z-20 flex w-full flex-col gap-4 rounded-10 bg-white p-4 shadow-generator [&:is([hidden])]:hidden',
			)}
			style={{ width: `${containerWidth}px` }}
		>
			<div className="flex flex-nowrap gap-4">
				<Switch
					visual="secondary"
					value={mode}
					onValueChange={(value) => {
						setMode(value as TColorPickerMode)
					}}
					itemList={[
						{
							value: 'solid',
							label: translations.switchSolidModeLabel,
						},
						{
							value: 'palette',
							label: translations.switchPaletteModeLabel,
						},
					]}
					equalWidthButtons
					className="grow"
				/>
				{isEyeDropperSupported ? <ButtonEyeDropper /> : null}
			</div>
			{mode === 'solid' ? (
				<SolidModeContent />
			) : (
				<ColorPalette setValue={setValue} />
			)}
		</ColorPickerPrimitive.Content>
	)
}
